import React, { useEffect, useMemo, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { PageHeader, Section, EmptyState } from "@/components/Shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Building2, Users, ShieldAlert, PauseCircle, PlayCircle, Search, ChevronRight, Trash2, History } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { ROLE_OPTIONS } from "@/lib/perms";
import { formatInt } from "@/lib/menu-utils";

export default function Platform() {
  const { user: me } = useAuth();
  const [summary, setSummary] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [drilldown, setDrilldown] = useState(null); // tenant object
  const [drillUsers, setDrillUsers] = useState([]);
  const [drillLoading, setDrillLoading] = useState(false);
  const [confirmSuspend, setConfirmSuspend] = useState(null); // tenant object
  const [confirmDeleteUser, setConfirmDeleteUser] = useState(null); // user object

  const [tab, setTab] = useState("orgs");
  const [audit, setAudit] = useState({ total: 0, items: [] });
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditFilter, setAuditFilter] = useState({ action: "", target_type: "" });

  const loadAudit = async () => {
    setAuditLoading(true);
    try {
      const params = { limit: 200 };
      if (auditFilter.action) params.action = auditFilter.action;
      if (auditFilter.target_type) params.target_type = auditFilter.target_type;
      const { data } = await api.get("/platform/audit-log", { params });
      setAudit(data);
    } catch (e) { toast.error(apiErrorMessage(e)); } finally { setAuditLoading(false); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: s }, { data: t }] = await Promise.all([
        api.get("/platform/summary"),
        api.get("/platform/tenants"),
      ]);
      setSummary(s);
      setTenants(t);
    } catch (e) { toast.error(apiErrorMessage(e)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { if (tab === "audit") loadAudit(); /* eslint-disable-next-line */ }, [tab, auditFilter]);

  const openTenant = async (t) => {
    setDrilldown(t);
    setDrillLoading(true);
    try {
      const { data } = await api.get(`/platform/tenants/${t.id}/users`);
      setDrillUsers(data);
    } catch (e) { toast.error(apiErrorMessage(e)); } finally { setDrillLoading(false); }
  };

  const refreshDrill = async () => {
    if (!drilldown) return;
    try {
      const { data } = await api.get(`/platform/tenants/${drilldown.id}/users`);
      setDrillUsers(data);
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const toggleUser = async (u, active) => {
    try {
      await api.put(`/platform/users/${u.id}`, { active });
      toast.success(active ? "Usuário reativado" : "Usuário desativado");
      refreshDrill(); load();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const changeUserRole = async (u, role) => {
    try {
      await api.put(`/platform/users/${u.id}`, { role });
      toast.success("Papel atualizado");
      refreshDrill(); load();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const deleteUser = async () => {
    try {
      await api.delete(`/platform/users/${confirmDeleteUser.id}`);
      toast.success("Usuário removido");
      setConfirmDeleteUser(null);
      refreshDrill(); load();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const toggleTenant = async (t, active) => {
    try {
      const { data } = await api.put(`/platform/tenants/${t.id}`, { active });
      toast.success(active ? "Organização reativada" : "Organização suspensa");
      setConfirmSuspend(null);
      setTenants((rows) => rows.map((r) => (r.id === t.id ? data : r)));
      if (drilldown?.id === t.id) setDrilldown(data);
      load();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return tenants;
    return tenants.filter((t) => t.name.toLowerCase().includes(term));
  }, [tenants, q]);

  return (
    <div data-testid="platform-page">
      <PageHeader
        title="Plataforma MenuPro"
        subtitle="Visão global de todas as organizações usando o MenuPro. Você pode suspender uma organização inteira ou desativar usuários específicos em caso de uso não autorizado."
      />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-5">
          <TabsTrigger value="orgs" data-testid="platform-tab-orgs"><Building2 size={14} className="mr-1"/> Organizações</TabsTrigger>
          <TabsTrigger value="audit" data-testid="platform-tab-audit"><History size={14} className="mr-1"/> Auditoria</TabsTrigger>
        </TabsList>

        <TabsContent value="orgs">

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <div className="mp-kpi" data-testid="kpi-tenants">
          <div className="mp-kpi-label flex items-center justify-between">
            Organizações <Building2 size={14} className="text-slate-400" />
          </div>
          <div className="mp-kpi-value">{loading ? "—" : formatInt(summary?.tenants_count)}</div>
          <div className="mp-kpi-sub">
            {summary?.active_tenants_count} ativas
            {summary?.suspended_tenants_count > 0 && (
              <span className="ml-2 text-amber-700 font-medium">· {summary?.suspended_tenants_count} suspensa(s)</span>
            )}
          </div>
        </div>
        <div className="mp-kpi" data-testid="kpi-users">
          <div className="mp-kpi-label flex items-center justify-between">Usuários totais <Users size={14} className="text-slate-400" /></div>
          <div className="mp-kpi-value">{loading ? "—" : formatInt(summary?.users_count)}</div>
          <div className="mp-kpi-sub">{summary?.active_users_count} ativos</div>
        </div>
        <div className="mp-kpi" data-testid="kpi-companies">
          <div className="mp-kpi-label">Empresas atendidas</div>
          <div className="mp-kpi-value">{loading ? "—" : formatInt(summary?.companies_count)}</div>
          <div className="mp-kpi-sub">somando todas as organizações</div>
        </div>
        <div className="mp-kpi" data-testid="kpi-avg">
          <div className="mp-kpi-label">Média usuários / org</div>
          <div className="mp-kpi-value">
            {loading || !summary?.tenants_count ? "—" : (summary.users_count / summary.tenants_count).toFixed(1)}
          </div>
          <div className="mp-kpi-sub">indicador de adoção</div>
        </div>
      </div>

      <Section
        title="Organizações"
        actions={
          <div className="relative">
            <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Buscar organização…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="h-8 pl-7 w-56"
              data-testid="platform-search-input"
            />
          </div>
        }
      >
        {loading ? (
          <div className="p-8 text-center text-slate-500 text-sm">Carregando…</div>
        ) : filtered.length === 0 ? (
          <EmptyState title="Nenhuma organização" description="Nada corresponde ao filtro." />
        ) : (
          <div className="overflow-x-auto">
            <table className="mp-table w-full" data-testid="platform-tenants-table">
              <thead>
                <tr>
                  <th>Organização</th>
                  <th className="mp-num">Usuários</th>
                  <th className="mp-num">Ativos</th>
                  <th className="mp-num">Admins</th>
                  <th className="mp-num">Empresas atendidas</th>
                  <th>Status</th>
                  <th>Criada em</th>
                  <th className="text-right">Ações</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((t) => (
                  <tr key={t.id} data-testid={`platform-tenant-row-${t.id}`}>
                    <td>
                      <button
                        onClick={() => openTenant(t)}
                        className="text-left font-medium text-slate-900 hover:text-emerald-800 flex items-center gap-1"
                        data-testid={`open-tenant-${t.id}`}
                      >
                        {t.name}
                        {t.id === me?.tenant_id && (
                          <span className="ml-2 text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800">Sua org</span>
                        )}
                      </button>
                    </td>
                    <td className="mp-num tabular-nums">{t.users_count}</td>
                    <td className="mp-num tabular-nums">{t.active_users_count}</td>
                    <td className="mp-num tabular-nums">{t.admins_count}</td>
                    <td className="mp-num tabular-nums">{t.companies_count}</td>
                    <td>
                      {t.active
                        ? <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200">Ativa</span>
                        : <span className="text-xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200">Suspensa</span>}
                    </td>
                    <td className="text-slate-600 text-xs">
                      {new Date(t.created_at).toLocaleDateString("pt-BR")}
                    </td>
                    <td>
                      <div className="flex items-center justify-end gap-1">
                        {t.active ? (
                          <Button
                            variant="ghost" size="sm"
                            className="h-8 text-amber-700 hover:text-amber-800"
                            onClick={() => setConfirmSuspend(t)}
                            disabled={t.id === me?.tenant_id}
                            data-testid={`suspend-tenant-${t.id}`}
                          >
                            <PauseCircle size={15} className="mr-1" /> Suspender
                          </Button>
                        ) : (
                          <Button
                            variant="ghost" size="sm"
                            className="h-8 text-emerald-700 hover:text-emerald-800"
                            onClick={() => toggleTenant(t, true)}
                            data-testid={`reactivate-tenant-${t.id}`}
                          >
                            <PlayCircle size={15} className="mr-1" /> Reativar
                          </Button>
                        )}
                        <Button
                          variant="outline" size="sm" className="h-8"
                          onClick={() => openTenant(t)}
                          data-testid={`view-tenant-${t.id}`}
                        >
                          Ver usuários <ChevronRight size={14} className="ml-1" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>
        </TabsContent>

        <TabsContent value="audit">
          <Section
            title="Log de auditoria"
            actions={
              <div className="flex items-center gap-2">
                <Select value={auditFilter.target_type || "__all__"} onValueChange={(v) => setAuditFilter({ ...auditFilter, target_type: v === "__all__" ? "" : v })}>
                  <SelectTrigger className="h-8 w-40" data-testid="audit-filter-type"><SelectValue placeholder="Tipo" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">Todos os tipos</SelectItem>
                    <SelectItem value="tenant">Organização</SelectItem>
                    <SelectItem value="user">Usuário</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={auditFilter.action || "__all__"} onValueChange={(v) => setAuditFilter({ ...auditFilter, action: v === "__all__" ? "" : v })}>
                  <SelectTrigger className="h-8 w-56" data-testid="audit-filter-action"><SelectValue placeholder="Ação" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">Todas as ações</SelectItem>
                    <SelectItem value="tenant.suspend">Suspendeu organização</SelectItem>
                    <SelectItem value="tenant.reactivate">Reativou organização</SelectItem>
                    <SelectItem value="user.deactivate">Desativou usuário</SelectItem>
                    <SelectItem value="user.reactivate">Reativou usuário</SelectItem>
                    <SelectItem value="user.role_change">Alterou papel</SelectItem>
                    <SelectItem value="user.delete">Removeu usuário</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            }
          >
            {auditLoading ? (
              <div className="p-8 text-center text-slate-500 text-sm">Carregando…</div>
            ) : audit.items.length === 0 ? (
              <EmptyState title="Nenhum evento registrado" description="Assim que você suspender, reativar, alterar ou remover algo, o registro aparecerá aqui." />
            ) : (
              <div className="overflow-x-auto">
                <table className="mp-table w-full" data-testid="audit-table">
                  <thead>
                    <tr>
                      <th className="w-44">Data / hora</th>
                      <th>Ator</th>
                      <th>Ação</th>
                      <th>Alvo</th>
                      <th>Detalhes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {audit.items.map((ev) => (
                      <tr key={ev.id} data-testid={`audit-row-${ev.id}`}>
                        <td className="text-slate-600 whitespace-nowrap text-xs tabular-nums">
                          {new Date(ev.ts).toLocaleString("pt-BR")}
                        </td>
                        <td>
                          <div className="font-medium text-slate-900">{ev.actor_name}</div>
                          <div className="text-xs text-slate-500">{ev.actor_email}</div>
                        </td>
                        <td>
                          <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${actionBadgeStyle(ev.action)}`}>
                            {ev.action_label || ev.action}
                          </span>
                        </td>
                        <td>
                          <div className="text-slate-900">{ev.target_label || ev.target_id}</div>
                          <div className="text-[11px] text-slate-500 uppercase tracking-wide">{ev.target_type}</div>
                        </td>
                        <td className="text-xs text-slate-600">{renderMeta(ev.meta)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="px-4 py-2 text-xs text-slate-500 border-t border-slate-200 bg-slate-50">
                  Exibindo {audit.items.length} de {audit.total} eventos.
                </div>
              </div>
            )}
          </Section>
        </TabsContent>
      </Tabs>

      {/* Drilldown dialog */}
      <Dialog open={!!drilldown} onOpenChange={(o) => !o && setDrilldown(null)}>
        <DialogContent className="bg-white max-w-3xl" data-testid="tenant-drilldown-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 size={18} />
              {drilldown?.name}
              {drilldown && !drilldown.active && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200">Suspensa</span>
              )}
            </DialogTitle>
          </DialogHeader>

          {drilldown && (
            <div className="text-xs text-slate-500 -mt-2">
              {formatInt(drilldown.users_count)} usuários · {formatInt(drilldown.active_users_count)} ativos · {formatInt(drilldown.admins_count)} administradores · {formatInt(drilldown.companies_count)} empresas atendidas
            </div>
          )}

          <div className="flex justify-end mb-2">
            {drilldown?.active ? (
              <Button
                variant="outline" size="sm"
                onClick={() => setConfirmSuspend(drilldown)}
                disabled={drilldown?.id === me?.tenant_id}
                className="text-amber-700"
                data-testid="suspend-tenant-from-drill"
              >
                <PauseCircle size={14} className="mr-1"/> Suspender organização
              </Button>
            ) : (
              <Button
                variant="outline" size="sm"
                onClick={() => toggleTenant(drilldown, true)}
                className="text-emerald-700"
                data-testid="reactivate-tenant-from-drill"
              >
                <PlayCircle size={14} className="mr-1"/> Reativar organização
              </Button>
            )}
          </div>

          {drillLoading ? (
            <div className="p-6 text-center text-slate-500 text-sm">Carregando usuários…</div>
          ) : (
            <div className="max-h-[420px] overflow-y-auto border border-slate-200 rounded-md">
              <table className="mp-table w-full">
                <thead>
                  <tr>
                    <th>Usuário</th>
                    <th>Email</th>
                    <th>Papel</th>
                    <th>Status</th>
                    <th className="text-right">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {drillUsers.map((u) => (
                    <tr key={u.id} data-testid={`drill-user-row-${u.id}`}>
                      <td>
                        <div className="font-medium text-slate-900">{u.name}</div>
                        {u.id === me?.id && <div className="text-[10px] text-emerald-800 uppercase">Você</div>}
                      </td>
                      <td className="text-slate-600">{u.email}</td>
                      <td>
                        <Select value={u.role} onValueChange={(v) => changeUserRole(u, v)} disabled={u.id === me?.id}>
                          <SelectTrigger className="h-8 w-40" data-testid={`drill-role-${u.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {ROLE_OPTIONS.map((r) => (
                              <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                      <td>
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={u.active}
                            onCheckedChange={(v) => toggleUser(u, v)}
                            disabled={u.id === me?.id}
                            data-testid={`drill-active-${u.id}`}
                          />
                          <span className="text-xs text-slate-600">{u.active ? "Ativo" : "Inativo"}</span>
                        </div>
                      </td>
                      <td>
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost" size="icon" className="h-8 w-8 text-red-600 disabled:opacity-30"
                            disabled={u.id === me?.id}
                            onClick={() => setConfirmDeleteUser(u)}
                            data-testid={`drill-delete-${u.id}`}
                          ><Trash2 size={15} /></Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setDrilldown(null)} data-testid="close-drilldown-btn">Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm suspend */}
      <AlertDialog open={!!confirmSuspend} onOpenChange={(o) => !o && setConfirmSuspend(null)}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <ShieldAlert size={18} className="text-amber-600" />
              Suspender organização?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Todos os usuários de <b>{confirmSuspend?.name}</b> perderão acesso ao MenuPro imediatamente. Os dados são preservados e o acesso pode ser reativado a qualquer momento.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => toggleTenant(confirmSuspend, false)}
              data-testid="confirm-suspend-tenant"
              className="bg-amber-600 hover:bg-amber-700 text-white"
            >
              Suspender acesso
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirm delete user */}
      <AlertDialog open={!!confirmDeleteUser} onOpenChange={(o) => !o && setConfirmDeleteUser(null)}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Remover usuário?</AlertDialogTitle>
            <AlertDialogDescription>
              O acesso de <b>{confirmDeleteUser?.email}</b> será revogado permanentemente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={deleteUser} data-testid="confirm-delete-user-platform" className="bg-red-600 hover:bg-red-700 text-white">Remover</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function actionBadgeStyle(action) {
  if (!action) return "bg-slate-50 text-slate-700 border-slate-200";
  if (action.startsWith("tenant.suspend") || action === "user.deactivate" || action === "user.delete")
    return "bg-amber-50 text-amber-800 border-amber-200";
  if (action.startsWith("tenant.reactivate") || action === "user.reactivate")
    return "bg-emerald-50 text-emerald-800 border-emerald-200";
  if (action === "user.role_change" || action === "tenant.rename")
    return "bg-sky-50 text-sky-800 border-sky-200";
  return "bg-slate-50 text-slate-700 border-slate-200";
}

function renderMeta(meta) {
  if (!meta || Object.keys(meta).length === 0) return "—";
  if (meta.from !== undefined && meta.to !== undefined) {
    return <span><span className="line-through text-slate-400">{String(meta.from) || "—"}</span> → <span className="font-medium">{String(meta.to)}</span></span>;
  }
  return Object.entries(meta).map(([k, v]) => `${k}: ${v}`).join(" · ");
}
